import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

import type { CityGroup, DistrictNode } from "@/shared/lib/amap/districtService";

/**
 * 可输入搜索的城市选择器(2026-08-31 用户反馈:全国城市纯下拉滚着找太麻烦)。
 * 只替换 SearchPanel 里行政区级联的「所在城市」一格;行政区服务失败的静态兜底分支不走这里。
 * 交互:点开 → 输入框过滤(按市名或省名匹配)→ 点选/回车选第一个匹配。
 */

/** 过滤纯函数(单测靶):省名命中带出全省城市,市名命中只留该市;空查询原样返回。 */
export function filterCityGroups(groups: CityGroup[], query: string): CityGroup[] {
  const q = query.trim();
  if (!q) return groups;
  const out: CityGroup[] = [];
  for (const g of groups) {
    if (g.province.includes(q)) {
      out.push(g);
      continue;
    }
    const cities = g.cities.filter((c) => c.name.includes(q));
    if (cities.length > 0) out.push({ province: g.province, cities });
  }
  return out;
}

interface Props {
  cityGroups: CityGroup[];
  selCityAdcode: string;
  onSelectCity: (adcode: string) => void;
}

export function CityCombobox({ cityGroups, selCityAdcode, onSelectCity }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo<DistrictNode | null>(() => {
    for (const g of cityGroups) for (const c of g.cities) if (c.adcode === selCityAdcode) return c;
    return null;
  }, [cityGroups, selCityAdcode]);

  const filtered = useMemo(() => filterCityGroups(cityGroups, query), [cityGroups, query]);

  const pick = (adcode: string) => {
    onSelectCity(adcode);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls="city-combobox-list"
          data-testid="city-select"
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={cn(!selected && "text-muted-foreground")}>
            {selected?.name ?? "选择城市"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="border-b p-2">
          <Input
            autoFocus
            className="h-8"
            placeholder="输入城市或省份名搜索"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const first = filtered[0]?.cities[0];
                if (first) pick(first.adcode);
              }
            }}
          />
        </div>
        <div id="city-combobox-list" role="listbox" className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">没有匹配的城市</p>
          )}
          {filtered.map((g) => (
            <div key={g.province}>
              {/* 直辖市组只有同名一项,不再重复渲染组头(与原 Select 同规矩) */}
              {!(g.cities.length === 1 && g.cities[0].name === g.province) && (
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {g.province}
                </p>
              )}
              {g.cities.map((c) => (
                <button
                  key={c.adcode}
                  type="button"
                  onClick={() => pick(c.adcode)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                    c.adcode === selCityAdcode && "bg-accent"
                  )}
                >
                  {c.name}
                  {c.adcode === selCityAdcode && <Check className="h-4 w-4" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
